import React, { useEffect, useState } from 'react'
import { ArrowUpIcon, CheckIcon } from 'lucide-react'

import { Button } from '@repo/ui'
import { useToggleVote } from 'hooks/api'
import { useSnackbar } from 'contexts'
import { VoteReasonModal } from './VoteReasonModal'

/**
 * Single vote-toggle button (VERA-407, ported from Vergence Phase 9.0).
 * Optimistic: the voted state flips immediately on click; the server settles
 * the truth a moment later. The raw vote count is NOT surfaced here — the
 * card-level PriorityIndicator does the relative-rank communication.
 *
 * Adding a vote opens VoteReasonModal to capture an optional "why" reason.
 * Removing a vote fires immediately with no modal.
 */
export const VoteButton = ({
  requestId,
  voted,
  integrationName,
}: {
  requestId: string
  voted: boolean
  integrationName: string
}) => {
  const { mutate, isPending } = useToggleVote()
  const { open: notification } = useSnackbar()
  const [optimistic, setOptimistic] = useState(voted)
  const [modalOpen, setModalOpen] = useState(false)

  // Reconcile to server truth after the list refetches.
  useEffect(() => setOptimistic(voted), [voted])

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (optimistic) {
      // Already voted — remove vote immediately, no modal.
      setOptimistic(false)
      mutate(
        { requestId },
        {
          onError: (err: any) => {
            setOptimistic(voted)
            notification({ type: 'error', title: err?.message || "Couldn't remove your vote. Please try again." })
          },
        },
      )
    } else {
      // Not yet voted — open the reason modal.
      setModalOpen(true)
    }
  }

  const handleModalSubmit = (reason: string) => {
    setModalOpen(false)
    setOptimistic(true)
    mutate(
      { requestId, reason: reason || undefined },
      {
        onError: (err: any) => {
          setOptimistic(voted)
          notification({ type: 'error', title: err?.message || "Couldn't record your vote. Please try again." })
        },
      },
    )
  }

  return (
    <>
      <Button
        variant={optimistic ? 'primary' : 'secondary'}
        disableElevation
        disabled={isPending}
        startIcon={optimistic ? <CheckIcon size={14} strokeWidth={2} /> : <ArrowUpIcon size={14} strokeWidth={2} />}
        onClick={handleClick}
      >
        {optimistic ? 'Voted' : 'Upvote'}
      </Button>

      <VoteReasonModal
        open={modalOpen}
        integrationName={integrationName}
        onSubmit={handleModalSubmit}
        onClose={() => setModalOpen(false)}
      />
    </>
  )
}
